import React, {
  FC,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import i18n, { BackendModule, FallbackLng } from "i18next";
import { I18NextInitializer } from "./I18NextInitializer";
import i18next from "i18next";
import { toI18nLng } from "./toI18nLng";

let initCount = 0;

export type I18NextContextType = {
  languages: Array<string>;
};

const I18NextContext = React.createContext({} as I18NextContextType);

export interface I18NextProviderProps {
  /**
   * The user-facing BCP-47 locale (e.g. `nb-NO`) used to seed
   * `i18next.init({ lng })` on first mount.
   *
   * **Init-only**: this prop is read exactly once, when the i18next
   * singleton is first initialized. Subsequent changes to the prop are
   * not honored — all runtime language changes go exclusively through
   * `useI18nLanguageSync` in `shared-ts-language`.
   *
   * The user-facing locale is mapped to the on-disk i18n file basename
   * via {@link toI18nLng} before being passed to `I18NextInitializer`.
   *
   * @see `docs/prd/2026/05/22/prd-i18n-language-mapping.md`
   */
  language?: string;
  languages: Array<string>;
  fallbackLng?: false | FallbackLng;
  // On-disk locale-file basenames the app supports (e.g. ["en", "no"]).
  // Forwarded to i18next so it normalizes variants like `nb-NO` → `no`
  // before issuing HTTP requests. See PRD i18n-locale-file-sync.
  supportedLngs?: Array<string>;
  debug?: boolean;
  // See I18NextInitializer for the meaning of the three cases (stable
  // string, "local-development", null). Required so each caller makes the
  // choice explicitly.
  defaultVersion: string | null;
  // Backends contributed to the front of the chained-backend chain (ahead
  // of the default `[LocalStorageBackend?, HttpApi]` chain). Forwarded
  // verbatim to {@link I18NextInitializer}. Matrikkel-agnostic: any library
  // can contribute a backend. Omitting this reproduces the default chain.
  // See prd-matrikkel-kodeliste-i18n-backend-plugin (ADR 0003).
  prependBackends?: Array<BackendModule>;
  // When `true` (the default), a guard backend errors loudly and skips the
  // HTTP load if a component calls `useTranslation()` without an explicit
  // namespace (which otherwise falls back to a nonexistent `translation`
  // namespace and 404s). Set to `false` only if the app legitimately serves a
  // `translation` namespace. See I18NextInitializer / MissingNamespaceGuardBackend.
  errorOnMissingNamespace?: boolean;
  children?: ReactNode;
}

export const I18NextProvider: FC<I18NextProviderProps> = (props) => {
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const doEffect = async () => {
      if (initCount === 0) {
        initCount++;
        const mappedLng =
          props.language != null
            ? toI18nLng(props.language, props.supportedLngs ?? [])
            : undefined;
        const i18n = await new I18NextInitializer({
          defaultVersion: props.defaultVersion,
          lng: mappedLng,
          fallbackLng: props.fallbackLng,
          supportedLngs: props.supportedLngs,
          debug: props.debug,
          prependBackends: props.prependBackends,
          errorOnMissingNamespace: props.errorOnMissingNamespace,
        }).init();

        setupI18nLogging(i18n);
        setInitialized(true);
        console.info("I18NextProvider has initialized i18n");
      } else if (i18next.isInitialized) {
        // Remount after init already happened elsewhere (HMR, parent remount):
        // sync local state with the module-level singleton.
        setInitialized(true);
      } else if (initCount > 2) {
        console.warn("I18NextProvider mounted more than twice");
      }
    };
    doEffect();

    return () => {
      console.log("I18NextProvider unmounted...");
    };
  }, [
    props.language,
    props.fallbackLng,
    props.languages,
    props.supportedLngs,
    props.debug,
    props.defaultVersion,
    props.prependBackends,
    props.errorOnMissingNamespace,
  ]);

  const contextValue: I18NextContextType = useMemo(() => {
    return { languages: props.languages };
  }, [props.languages]);

  if (!initialized || !i18n.isInitialized) {
    return null;
  }

  return (
    <I18NextContext.Provider value={contextValue}>
      {props.children}
    </I18NextContext.Provider>
  );
};

export const useI18Next = (): I18NextContextType => {
  const context = useContext(I18NextContext);
  if (context == null) {
    throw new Error("useI18Next must be used within I18NextProvider");
  }
  return context;
};


function setupI18nLogging(i18n: typeof i18next) {
  const byNs: Record<string, Record<string, number>> = {};
  let timer: ReturnType<typeof setTimeout> | null = null;

  i18n.on("loaded", (loaded) => {
    for (const [lng, namespaces] of Object.entries(loaded)) {
      for (const ns of Object.keys(namespaces)) {
        const bundle = i18n.getResourceBundle(lng, ns);
        const count = bundle ? countKeys(bundle as object) : 0;
        if (!byNs[ns]) byNs[ns] = {};
        byNs[ns][lng] = count;
      }
    }

    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      console.group("🌐 i18next loaded namespaces");
      for (const [namespace, langCounts] of Object.entries(byNs)) {
        const counts = Object.entries(langCounts)
          .map(([l, c]) => `${l}:${c}`)
          .join(", ");
        console.info(`${namespace} [${counts}]`);
      }
      console.groupEnd();
    }, 1000);
  });
}

function countKeys(obj: object): number {
  return Object.entries(obj).reduce((acc, [, value]) => {
    if (typeof value === "object" && value !== null) {
      return acc + countKeys(value);
    }
    return acc + 1;
  }, 0);
}
